import React, { useState } from 'react';
import { AdminRoute } from './AdminShell';
import { LayoutDashboard, Users, BookOpen, ShieldCheck, Wrench, Activity, Settings, ChevronRight, HelpCircle, ArrowLeft, Sparkles, LogOut, GitBranch } from 'lucide-react';
interface NavItem {
  id: AdminRoute;
  label: string;
  icon: React.ReactNode;
  description?: string;
}
interface NavGroup {
  label: string;
  items: NavItem[];
}
const NAV_GROUPS: NavGroup[] = [{
  label: 'Overzicht',
  items: [{
    id: 'dashboard',
    label: 'Dashboard',
    icon: <LayoutDashboard className="w-4 h-4" data-id="element-3035" />,
    description: 'Systeem overzicht'
  }]
}, {
  label: 'Gebruikers',
  items: [{
    id: 'users',
    label: 'Gebruikers',
    icon: <Users className="w-4 h-4" data-id="element-3036" />,
    description: 'Alle accounts beheren'
  }]
}, {
  label: 'Inhoud',
  items: [{
    id: 'question-bank',
    label: 'Vragenbank',
    icon: <BookOpen className="w-4 h-4" data-id="element-3037" />,
    description: 'Alle vragen & seeds'
  }, {
    id: 'governance',
    label: 'Governance & Kwaliteit',
    icon: <ShieldCheck className="w-4 h-4" data-id="element-3038" />,
    description: 'Pipeline, validatie & quality workspace'
  }, {
    id: 'support-content',
    label: 'Ondersteuning',
    icon: <HelpCircle className="w-4 h-4" data-id="element-3039" />,
    description: 'Uitleg & hints'
  }, {
    id: 'ldg-graph',
    label: 'Leerpad Graaf',
    icon: <GitBranch className="w-4 h-4" data-id="element-3040" />,
    description: 'Visuele LDG verkenner'
  }, {
    id: 'mascot',
    label: 'Mascotte Djay',
    icon: <Sparkles className="w-4 h-4" data-id="element-3041" />,
    description: 'Djay design & preview'
  }]
}, {
  label: 'Systeem',
  items: [{
    id: 'diagnostics',
    label: 'Diagnostiek',
    icon: <Wrench className="w-4 h-4" data-id="element-3042" />,
    description: 'Seeds, taxonomie & console'
  }, {
    id: 'logs',
    label: 'Activiteitenlog',
    icon: <Activity className="w-4 h-4" data-id="element-3043" />,
    description: 'Sessie logs'
  }]
}];
interface AdminSidebarProps {
  activeRoute: AdminRoute;
  onNavigate: (route: AdminRoute) => void;
  onLogout?: () => void;
  onReturnHome?: () => void;
}
export function AdminSidebar({
  activeRoute,
  onNavigate,
  onLogout,
  onReturnHome
}: AdminSidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  return <aside className={`flex flex-col bg-white border-r border-gray-200 transition-all duration-200 flex-shrink-0 h-full ${collapsed ? 'w-14' : 'w-56'}`} data-id="element-3044">
      {/* Header */}
      <div className={`flex items-center border-b border-gray-100 flex-shrink-0 ${collapsed ? 'justify-center px-2 py-4' : 'justify-between px-4 py-4'}`} data-id="element-3045">
        {!collapsed && <div className="flex items-center gap-2.5" data-id="element-3046">
            <div className="w-7 h-7 bg-gradient-to-br from-indigo-600 to-violet-600 rounded-lg flex items-center justify-center shadow-sm" data-id="element-3047">
              <span className="text-white font-black text-sm" data-id="element-3048">M</span>
            </div>
            <div data-id="element-3049">
              <div className="text-sm font-bold text-gray-900 leading-tight" data-id="element-3050">
                Admin
              </div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold" data-id="element-3051">
                Beheercentrum
              </div>
            </div>
          </div>}
        <button onClick={() => setCollapsed(c => !c)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none" aria-label={collapsed ? 'Uitklappen' : 'Inklappen'} data-id="element-3052">
          <ChevronRight className={`w-4 h-4 transition-transform ${collapsed ? '' : 'rotate-180'}`} data-id="element-3053" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2" data-id="element-3054">
        {NAV_GROUPS.map((group, index) => <div key={group.label} className="mb-4" data-id="element-3055">
            {!collapsed && <>
                {index > 0 && <div className="h-px bg-gray-100 mx-2 mb-3" data-id="element-3056" />}
                <div className="px-2 mb-1.5" data-id="element-3057">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-3058">
                    {group.label}
                  </span>
                </div>
              </>}
            <div className="space-y-0.5" data-id="element-3059">
              {group.items.map(item => {
            const isActive = activeRoute === item.id;
            return <button key={item.id} onClick={() => onNavigate(item.id)} title={collapsed ? item.label : undefined} className={`w-full flex items-center gap-2.5 rounded-lg transition-colors relative focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none ${collapsed ? 'justify-center px-2 py-2.5' : 'px-2.5 py-2'} ${isActive ? 'bg-blue-50/50 text-blue-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`} data-id="element-3060">
                    {isActive && <div className="absolute left-0 top-1.5 bottom-1.5 w-1 bg-blue-600 rounded-r-full" data-id="element-3061" />}
                    <span className={`flex-shrink-0 ${isActive ? 'text-blue-600' : ''}`} data-id="element-3062">
                      {item.icon}
                    </span>
                    {!collapsed && <span className={`text-sm truncate ${isActive ? 'font-medium' : ''}`} data-id="element-3063">
                        {item.label}
                      </span>}
                  </button>;
          })}
            </div>
          </div>)}
      </nav>

      {/* Footer */}
      <div className={`flex-shrink-0 border-t border-gray-100 ${collapsed ? 'p-2' : 'p-3'}`} data-id="element-3064">
        {/* Settings button */}
        <button onClick={() => onNavigate('settings')} title={collapsed ? 'Instellingen' : undefined} className={`w-full flex items-center gap-2.5 rounded-lg transition-colors mb-3 relative focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none ${collapsed ? 'justify-center px-2 py-2.5' : 'px-2.5 py-2'} ${activeRoute === 'settings' ? 'bg-blue-50/50 text-blue-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`} data-id="element-3065">
          {activeRoute === 'settings' && <div className="absolute left-0 top-1.5 bottom-1.5 w-1 bg-blue-600 rounded-r-full" data-id="element-3066" />}
          <Settings className={`w-4 h-4 ${activeRoute === 'settings' ? 'text-blue-600' : ''}`} data-id="element-3067" />
          {!collapsed && <span className={`text-sm ${activeRoute === 'settings' ? 'font-medium' : ''}`} data-id="element-3068">
              Instellingen
            </span>}
        </button>

        {onLogout && <button onClick={onLogout} className={`w-full flex items-center justify-center gap-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:outline-none ${collapsed ? 'p-2' : 'px-3 py-2 text-sm font-medium'}`} title={collapsed ? 'Uitloggen' : undefined} data-id="element-3069">
            <LogOut className="w-4 h-4" data-id="element-3070" />
            {!collapsed && 'Uitloggen'}
          </button>}

        {onReturnHome && <button onClick={onReturnHome} className={`w-full flex items-center justify-center gap-2 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-50 transition-colors focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none ${collapsed ? 'p-2' : 'px-3 py-2 text-sm font-medium'}`} title={collapsed ? 'Terug naar Home' : undefined} data-id="element-3071">
            <ArrowLeft className="w-4 h-4" data-id="element-3072" />
            {!collapsed && 'Terug naar Home'}
          </button>}
      </div>
    </aside>;
}